// Core types shared across the user portal.
// Extend with your solution's primary-entity types (badge, NFT, asset, ...)
// as you implement features.

export interface User {
  userId: string;
  email: string;
  name: string;
  plan: Plan;
  status: 'active' | 'paused' | 'terminated';
  createdAt: number | null;
}

export type Plan = 'free' | 'mid' | 'top';

// Display copy (name, description, features) is sourced from the
// `pricing` i18n namespace (per-locale translations). Only numeric
// limits referenced by app logic live here.
export const PLAN_DETAILS: Record<Plan, { name: string; price: string; apiKeyLimit: number }> = {
  free:  { name: 'Free',    price: '$0',  apiKeyLimit: 1  },
  mid:   { name: 'Starter', price: '$9',  apiKeyLimit: 5  },
  top:   { name: 'Pro',     price: '$29', apiKeyLimit: 20 }
};

export type ApiKeyScope = 'read' | 'write';

export interface ApiKey {
  keyPrefix: string;
  keyName: string;
  scopes?: ApiKeyScope[];
  createdAt: number;
  lastUsedAt: number | null;
  expiresAt: number | null;
}

export interface ApiKeyCreateResponse {
  apiKey: string;
  keyPrefix: string;
  keyName: string;
  scopes?: ApiKeyScope[];
  createdAt: number;
  expiresAt: number | null;
}

export type WebhookEvent =
  | 'credential.issued'
  | 'credential.delivered'
  | 'credential.revoked'
  | 'credential.viewed';

export const WEBHOOK_EVENTS: WebhookEvent[] = [
  'credential.issued',
  'credential.delivered',
  'credential.revoked',
  'credential.viewed',
];

export interface Webhook {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  failureCount: number;
  createdAt: number;
  lastStatus: number | null;
  lastDeliveryAt: number | null;
}

export interface WebhookCreateResponse {
  id: string;
  url: string;
  events: string[];
  secret: string;
}

export interface IssuerSettings {
  emailFromName?: string;
  replyTo?: string;
  brandColor?: string;
}

export interface SupportTicket {
  ticketId: string;
  subject: string;
  category: string;
  status: 'open' | 'resolved' | 'closed';
  hasReply: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface TicketReply {
  from: 'user' | 'admin';
  message: string;
  timestamp: number;
}

export interface SupportTicketDetail extends SupportTicket {
  message: string;
  replies: TicketReply[];
}

export interface SupportTicketCreateResponse {
  ticketId: string;
}

// Badge types

export interface Issuer {
  '@context'?: string;
  id: string;
  name: string;
  url: string;
  email: string;
  image?: string;
  type?: string;
  verified?: boolean;
  /** Numeric LinkedIn Page (Organization) ID — enables "Add to Profile" deep link. */
  linkedinOrganizationId?: string;
}

export interface BadgeCriteria {
  narrative: string | string[];
}

export interface Badge {
  '@context'?: string;
  type?: string;
  id: string;
  name: string;
  description: string;
  image: string;
  criteria: BadgeCriteria;
  issuer: Issuer;
  shareText?: string;
  /** Serialized Fabric canvas state captured at design time. Optional — badges
   *  created before this field shipped have no value here, in which case the
   *  editor falls back to the rendered IPFS PNG (read-only raster). */
  designJson?: string | null;
}

/** A saved account badge template — a reusable design. The list endpoint omits
 *  designJson (fetched via getTemplate) to keep responses lean. */
export interface BadgeTemplate {
  id: string;
  name: string;
  image?: string | null;
  createdAt?: number;
  designJson?: string;
}

/** A server-side badge draft (badge-in-progress). The list is lite; getDraft
 *  returns the full record for resuming. */
export interface DraftListItem {
  id: string;
  name: string;
  image?: string | null;
  updatedAt?: number;
}
export interface Draft {
  id: string;
  name: string;
  description?: string;
  criteria?: string;
  issuerId?: string;
  shareText?: string;
  shareTextTouched?: boolean;
  designJson?: string;
  image?: string | null;
  createdAt?: number;
  updatedAt?: number;
}

export interface AwardRecipient {
  type?: string;
  hashed?: boolean;
  salt?: string;
  identity?: string;
  name: string;
}

export interface Award {
  '@context'?: string;
  type?: string;
  id: string;
  recipient: AwardRecipient;
  badge: Badge;
  verification?: { type: string };
  issuedOn: string;
  expires?: string;
  image: string;
  recipientEmail?: string | null;
  revoked?: boolean;
  revocationReason?: string;
  /** ms epoch when a notification email was last sent for this award; null/absent = never emailed. */
  notifiedAt?: number | null;
}

export interface BulkSendJob {
  jobId: string;
  /** What the job does to each selected credential. */
  action?: 'send' | 'revoke' | 'delete';
  status: 'queued' | 'running' | 'done' | 'failed';
  total: number;
  /** Count of SUCCEEDED items (emailed / revoked / deleted, per `action`). */
  sent: number;
  failed: number;
  skipped: number;
  errors: string[];
  createdAt: number;
  updatedAt: number;
  /** How the recipient set was chosen: an explicit id list or a resolved filter. */
  scope?: {
    kind?: 'ids' | 'filter';
    count?: number;
    capped?: boolean;
    filter?: { badgeId?: string; notEmailedOnly?: boolean; search?: string };
  } | null;
}

export type BulkSendItemOutcome = 'pending' | 'sent' | 'revoked' | 'deleted' | 'skipped' | 'failed';
export type BulkSendItemReason =
  | 'not_owner' | 'no_email' | 'revoked' | 'already_notified' | 'already_revoked' | 'send_error' | 'action_error';

/** One recipient's row in a bulk-send job's intention→execution ledger. */
export interface BulkSendItem {
  awardId: string;
  outcome: BulkSendItemOutcome;
  reason: BulkSendItemReason | null;
  messageId: string | null;
  recipientEmail: string | null;
  badgeGuid: string | null;
  detail: string | null;
  updatedAt: number | null;
}

export interface AwardListResponse {
  count: number;
  items: Award[];
  lastEvaluatedKey?: string;
  total?: number;
}

// --- Plan usage / limits (drives the limit-reached banners) ---

// Per-award email delivery status (GET /awards/{id}/delivery)
export interface AwardDelivery {
  tracked: boolean;
  status: 'send' | 'delivery' | 'open' | 'bounce' | 'complaint' | 'reject' | null;
  sentAt?: number | null;
  deliveredAt?: number | null;
  openedAt?: number | null;
  bounced?: boolean;
}

// Aggregate email engagement for the issuer (GET /me/email-stats) — Analytics page.
export interface EmailStats {
  range: string;
  totals: { sent: number; delivered: number; opened: number; bounced: number; complained: number };
  rates: { delivery: number; open: number; bounce: number; complaint: number };
  series: { date: string; sent: number; opened: number }[];
  /** Bounces split by SES type (Permanent / Transient / Undetermined). */
  bounceReasons: Record<string, number>;
}

// Issuer analytics Overview (GET /me/analytics) — the expanded Analytics page.
export interface AnalyticsResponse {
  range: string;
  kpis: {
    issued: number; delivered: number; views: number; shares: number;
    viewedAwards: number; sharedAwards: number;
    deliveryRate: number; viewRate: number; shareRate: number;
    prev: { issued: number; delivered: number; views: number; shares: number };
  };
  series: { date: string; issued: number; delivered: number; view: number; share: number; verify: number }[];
  funnel: { issued: number; delivered: number; viewed: number; shared: number };
  sharesByNetwork: Record<string, number>;
  actions: Record<string, number>;
  totals: Record<string, number>;
  topBadges: { badgeId: string; name: string; issued: number; views: number; shares: number }[];
  topRecipients: { name: string; views: number; shares: number }[];
  /** Viewer geography (country code → engagement), derived from the viewer's timezone. */
  geography: { cc: string; views: number; shares: number }[];
  activity: { event: string; network: string; recipient: string; badge: string; at: number }[];
}

// Unified activity timeline (GET /me/activity)
export interface ActivityItem {
  ts: number;
  kind: 'engagement' | 'email' | 'portal';
  event: string;
  network?: string | null;
  recipient?: string | null;
  badge?: string | null;
  detail?: string | null;
  page?: string | null;
  label?: string | null;
  actor?: string | null;
}
export interface ActivityResponse {
  items: ActivityItem[];
  nextBefore: number | null;
}

// D5 — recipient engagement rollup (GET /engagement)
export interface EngagementResponse {
  generatedAt: number;
  awardsTotal: number;
  awardsWithEngagement: number;
  totals: {
    views: number;
    shares: number;
    downloads: number;
    linkedinAdds: number;
    verifyClicks: number;
  };
  topAwards: { guid: string; badge: string; views: number; shares: number }[];
}

export interface UsageResponse {
  plan: Plan;
  /** null = unlimited (e.g. Pro issuers/badges). Use Number.MAX_SAFE_INTEGER if you prefer numbers. */
  limits: {
    issuers: number | null;
    badges: number | null;
    apiKeys: number;
    awardsPerYear: number;
  };
  usage: {
    issuers: number;
    badges: number;
    apiKeys: number;
    /** Credentials issued in the account's current membership year (resets on the registration anniversary). */
    awardsThisYear: number;
  };
  /** ISO timestamp of the next anniversary reset for the yearly credential pool. */
  yearResetAt: string;
  /**
   * Timestamp of the account's first REAL credential, or null. Honest activation
   * signal: set only by the real issue path (Award_create), NOT by the auto-seed
   * sample award — so unlike usage.awardsThisYear it never counts the sample.
   */
  activatedAt?: number | null;
  /** True once the auto-seed / starter-kit [Sample] issuer+badge+award exists for the account. */
  starterKitSeeded?: boolean;
}

// ── Team members (seats & roles) ────────────────────────────────────────────
export type TeamRole = 'admin' | 'editor' | 'viewer';

export interface TeamMember {
  id: string;
  accountId: string;
  memberId: string | null;
  email: string;
  /** 'owner' marks the synthetic account-owner row (read-only, first in the list). */
  role: TeamRole | 'owner';
  status: 'pending' | 'active';
  invitedAt: number;
  acceptedAt: number | null;
  invitedBy: string | null;
  isOwner?: boolean;
}

export interface MembersResponse {
  /** The caller's own role in this account. */
  role: 'owner' | TeamRole;
  members: TeamMember[];
}

export interface InviteResponse {
  inviteId: string;
  email: string;
  role: TeamRole;
  status: string;
  acceptUrl: string;
}

/** Public invite preview (GET /members/{inviteId}) shown on the accept page. */
export interface InvitePreview {
  email: string;
  role: string;
  inviterName: string;
  status: string;
}
